import axios from 'axios';

// Configuration de l'API
const api = axios.create({
  baseURL: 'https://inza-backend.sajholding.org/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Ajouter un mock token pour les tests
    if (!token) {
      config.headers['X-Mock-Token'] = 'mock-access-token';
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Rediriger vers la page de login si non authentifié
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Service de paiement
export const paymentService = {
  // Initier un paiement Wave
  async initiateWavePayment(orderData) {
    try {
      console.log('=== initiateWavePayment appelé ===');
      console.log('orderData:', orderData);
      console.log('baseURL:', api.defaults.baseURL);
      
      const requestData = {
        order_id: orderData.orderId,
        amount: orderData.amount,
        phone: orderData.phone,
        customer_info: orderData.customerInfo,
        items: orderData.items,
        delivery_address: orderData.deliveryAddress,
        currency: 'XOF'
      };
      
      console.log('requestData:', requestData);
      console.log('URL complète:', api.defaults.baseURL + '/payments/wave/initiate');
      
      const response = await api.post('/payments/wave/initiate/', requestData);
      
      console.log('Réponse API:', response);
      console.log('Réponse data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du paiement Wave:', error);
      throw error;
    }
  },

  // Vérifier le statut d'un paiement
  async checkPaymentStatus(paymentId) {
    try {
      const response = await api.get(`/payments/status/${paymentId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
      throw error;
    }
  },

  // Confirmer une commande avec paiement après livraison
  async confirmCashOnDelivery(orderData) {
    try {
      const response = await api.post('/orders/cash-on-delivery', {
        customer_info: orderData.customerInfo,
        items: orderData.items,
        total_amount: orderData.amount,
        delivery_address: orderData.deliveryAddress
      });
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la confirmation de la commande:', error);
      throw error;
    }
  },

  // Polling du statut de paiement (pour Wave)
  async pollPaymentStatus(paymentId, maxAttempts = 20, interval = 3000) {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        const status = await this.checkPaymentStatus(paymentId);
        
        if (status.status === 'completed' || status.status === 'failed') {
          return status;
        }
        
        // Attendre avant la prochaine vérification
        await new Promise(resolve => setTimeout(resolve, interval));
        attempts++;
      } catch (error) {
        console.error(`Erreur polling paiement (tentative ${attempts + 1}):`, error);
        if (attempts === maxAttempts - 1) throw error;
        attempts++;
      }
    }
    
    throw new Error('Timeout lors de la vérification du statut de paiement');
  }
};

// Service de notification
export const notificationService = {
  // Afficher une notification de succès
  showSuccess(message, duration = 5000) {
    return this.showNotification(message, 'success', duration);
  },

  // Afficher une notification d'erreur
  showError(message, duration = 7000) {
    return this.showNotification(message, 'error', duration);
  },

  // Afficher une notification d'information
  showInfo(message, duration = 4000) {
    return this.showNotification(message, 'info', duration);
  },

  // Afficher une notification
  showNotification(message, type = 'info', duration = 5000) {
    // Créer un élément de notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      color: white;
      font-weight: 500;
      z-index: 9999;
      max-width: 400px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transition: all 0.3s ease;
      transform: translateX(100%);
    `;

    // Couleurs selon le type
    const colors = {
      success: '#28a745',
      error: '#dc3545',
      info: '#17a2b8',
      warning: '#ffc107'
    };
    
    notification.style.backgroundColor = colors[type] || colors.info;
    notification.textContent = message;

    // Ajouter au DOM
    document.body.appendChild(notification);

    // Animation d'entrée
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);

    // Auto-suppression
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, duration);

    return notification;
  }
};

// Service de validation
export const validationService = {
  // Valider un numéro de téléphone CI
  validateCIPhone(phone) {
    if (!phone) return { valid: false, message: 'Le numéro de téléphone est requis' };
    
    // Accepte tous les formats ivoiriens: 07XX, 05XX, 01XX, etc.
    const phoneRegex = /^(\+225|00225)?(0[1-7]\d{8}|[1-7]\d{8})$/;
    const cleaned = phone.replace(/\s/g, '');
    
    if (!phoneRegex.test(cleaned)) {
      return {
        valid: false,
        message: 'Format invalide. Ex: 07XX XX XX XX, 05XX XX XX XX ou +22507XX XX XX XX'
      };
    }
    
    return { valid: true, normalized: this.normalizePhone(cleaned) };
  },

  // Normaliser un numéro de téléphone
  normalizePhone(phone) {
    let cleaned = phone.replace(/\s/g, '');
    
    // Remplacer 00225 par +225
    if (cleaned.startsWith('00225')) {
      cleaned = cleaned.replace(/^00225/, '+225');
    }
    
    // Ajouter +225 si absent
    if (!cleaned.startsWith('+225')) {
      if (cleaned.length === 10) {
        // Format: 07XXXXXXXX
        return '+225' + cleaned;
      } else if (cleaned.length === 9) {
        // Format: 7XXXXXXXX
        return '+2250' + cleaned;
      }
    }
    
    return cleaned;
  },

  // Valider les informations client
  validateCustomerInfo(customerInfo) {
    const errors = [];
    
    if (!customerInfo.firstName || customerInfo.firstName.trim().length < 2) {
      errors.push('Le prénom est requis (minimum 2 caractères)');
    }
    
    if (!customerInfo.lastName || customerInfo.lastName.trim().length < 2) {
      errors.push('Le nom est requis (minimum 2 caractères)');
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (customerInfo.email && !emailRegex.test(customerInfo.email)) {
      errors.push('L\'adresse email est invalide');
    }
    
    const phoneValidation = this.validateCIPhone(customerInfo.phone);
    if (!phoneValidation.valid) {
      errors.push(phoneValidation.message);
    }
    
    return {
      valid: errors.length === 0,
      errors,
      normalizedPhone: phoneValidation.normalized
    };
  }
};
